import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { invoke } from '@tauri-apps/api/core';

import { T2MED_COMMAND_PREFIX } from '../types';

async function invokeT2med(
  command: string,
  payload: Record<string, unknown> = {}
): Promise<void> {
  if (!isTauri()) return;
  try {
    await invoke(`${T2MED_COMMAND_PREFIX}${command}`, payload);
  } catch (error) {
    // T2med is optional functionality — log (message only, payloads can
    // carry patient context) and degrade instead of crashing the caller.
    const message = error instanceof Error ? error.message : String(error);
    logger.error(`[t2med] plugin command ${command} failed`, message);
  }
}

/**
 * TS-side bridge to `tauri-plugin-t2med`.
 *
 * The plugin holds all secrets (APS launch token, X-API-Key) in Rust
 * memory only — nothing sensitive crosses this boundary except the
 * Heidi cloud JWT we push down.
 */
export const t2medBridge = {
  /** Push the Heidi cloud JWT after login and on every refresh. */
  async setCloudAuthToken(token: string): Promise<void> {
    await invokeT2med('set_cloud_auth_token', { token });
  },

  /** Push the ml-scribe origin (scheme + host, no path). */
  async setCloudBaseUrl(baseUrl: string): Promise<void> {
    await invokeT2med('set_cloud_base_url', { baseUrl });
  },

  /**
   * Drop cloud auth AND all T2med launch state (APS secrets, pending
   * ACKs) — call on logout / account switch.
   */
  async clearCloudAuthToken(): Promise<void> {
    await invokeT2med('clear_cloud_auth_token');
  },

  /** Flip the writeback poller to its fast cadence (user in a session). */
  async activateSession(): Promise<void> {
    await invokeT2med('activate_session');
  },

  /** Back to the slow background cadence. */
  async deactivateSession(): Promise<void> {
    await invokeT2med('deactivate_session');
  },

  /** Ask the poller to run now (e.g. right after a note push). */
  async triggerWritebackPoll(): Promise<void> {
    await invokeT2med('trigger_writeback_poll');
  },
};
